import { APIv4 as API } from 'src/libs/axios';
import { alertError } from 'src/libs/toastify';
import { BaseProductSet, ProductSet } from '../PromotionsPage/services';
import { omit } from 'lodash';
import { Pagination } from 'src/components/_shared/types';
import { FilterOption } from 'src/utils/getParams';

import {
  PromoCodeType,
  PromoCodeConditionType,
  getPromoCodeTypeFlags,
} from './constants';

export {
  PromoCodeType,
  PromoCodeConditionType,
  PromoCodeTypes,
  PromoCodeConditionTypes,
  getPromoCodeTypeFlags,
  getPromoCodeConditionTypeFlags,
} from './constants';

export type BasePromoCode = {
  id: number;
  name: string;
  count: number;
  type: PromoCodeType;
  lifetime: null | number;
  isActive: boolean;
  forApplications: boolean;
  forKiosk: boolean;
  forSite: boolean;
  forOfflineUse: boolean;
};

type PromoCode = {
  id: number;
  name: string;
  nameForCustomer: string | null;
  type: PromoCodeType;
  conditionType: PromoCodeConditionType;
  value: string;
  lifetime: number | null;
  isActive: boolean;
  isPublic: boolean | null;
  forSite: boolean;
  forApplications: boolean;
  forKiosk: boolean;
  forOfflineUse: boolean;
  forPickup: boolean;
  forDelivery: boolean;
  forDeliveryNoLimit: boolean;
  oneTimePerUser: boolean;
  forBirthday: boolean;
  forFirstOrder: boolean;
  includePromotionsItemsInMinPrice: boolean;
  minPrice: number | null;
  deliveryCount: number | null;
  discount: number | null;
  conditions: BaseProductSet[];
  presents: BaseProductSet[];
  franchisee: { id: number; name: string } | null;
  shops: { id: number; name: string }[];
  cities: { id: number; name: string }[];
};

export type PromoCodeValues = Omit<
  PromoCode,
  'id' | 'conditions' | 'presents'
> & {
  hasDiscount: boolean;
  hasMinPrice: boolean;
  conditions: ProductSet[];
  presents: ProductSet[];
};

type PromoCodeValue = {
  customerId: number | null;
  startDate: string | null;
  valuesCount: number | null;
  commentary: string;
  isRus: boolean;
  isEng: boolean;
  isNum: boolean;
};

type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  patronymic: string;
  birthDay: string;
  phoneNumber: string;
  email: string;
  lastActive: string;
  tickets: number;
  isActive: boolean;
};

export type PromoCodeValueValues = Omit<PromoCodeValue, 'customerId'> & {
  customer: Customer | null;
  type: PromoCode['type'];
};

export type ValuesPromoCode = {
  id: number;
  value: string;
  customerPhone: string;
  commentary: string;
  startDate: string;
  expirationDate: string;
};
export const fetchPromoCodeValues = (params: { id: number; query: string }) =>
  API.get<Pagination<ValuesPromoCode>>(
    `/promocodes/${params.id}/values?${params.query}`
  )
    .then(({ data }) => data)
    .catch((err) =>
      alertError(err, `Ошибка получения списка значений промокода`)
    );

type HistoryPromoCode = {
  promocodeValue: string;
  customerPhone: string;
  orderId: number | null;
  usageDateTime: string;
};

export const fetchPromoCodeHistory = (params: { id: number; query: string }) =>
  API.get<Pagination<HistoryPromoCode>>(
    `/promocodes/${params.id}/history?${params.query}`
  )
    .then(({ data }) => data)
    .catch((err) =>
      alertError(err, `Ошибка получения истории использования промокода`)
    );

type SearchParams = {
  page: number;
  rowsPerPage: number;
  search: string;
  franchiseeId: number | null;
  cityId: number | null;
  type: number;
  isActive?: boolean;
};

const adaptParams = (params: Partial<SearchParams>) => {
  const filters = [];

  if (params.search) {
    filters.push({
      ColumnName: 'name',
      FilterOption: FilterOption.CONTAINS,
      filterValue: params.search,
    });
  }

  if (params.type) {
    filters.push({
      ColumnName: 'type',
      FilterOption: FilterOption.EQUAL,
      filterValue: params.type,
    });
  }

  if (params.isActive !== undefined) {
    filters.push({
      ColumnName: 'isActive',
      FilterOption: FilterOption.EQUAL,
      filterValue: params.isActive,
    });
  }

  const result: Record<string, any> = {
    pageNumber: params.page ?? undefined,
    pageSize: params.rowsPerPage ?? undefined,
    franchiseeId: params.franchiseeId ?? undefined,
    cityId: params?.cityId ?? undefined,
    'SortingParams[0].SortOrder': 2,
    'SortingParams[0].ColumnName': 'id',
  };

  filters.forEach((filter, index) => {
    result[`FilterParams[${index}].ColumnName`] = filter.ColumnName;
    result[`FilterParams[${index}].FilterOption`] = filter.FilterOption;
    result[`FilterParams[${index}].filterValue`] = filter.filterValue;
  });

  return result;
};

export const fetchPromoCodes = (
  params: Partial<SearchParams>
): Promise<Pagination<BasePromoCode & { restricted: boolean }>> => {
  return API.get(`/promocodes/paged`, { params: adaptParams(params) })
    .then((response) => response.data)
    .catch((error) => alertError(error, `Ошибка получения списка промокодов`));
};

export const fetchAllPromoCodes = () =>
  API.get<(BasePromoCode & { restricted: boolean })[]>(`/promocodes`)
    .then(({ data }) => data.reverse())
    .catch((err) => alertError(err, `Ошибка получения списка промокодов`));

export const fetchPromoCode = ({ id }: { id: number }) =>
  API.get<PromoCode>(`/promocodes/${id}`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, `Ошибка получения промокода`));

const adaptPromoCode = (values: PromoCodeValues, isNew?: boolean) => {
  const {
    nameForCustomer,
    conditions,
    presents,
    hasDiscount,
    hasMinPrice,
    value,
    franchisee,
    cities,
    shops,
    ...rest
  } = values;
  const { isGeneral } = getPromoCodeTypeFlags(values.type);

  return {
    ...rest,
    nameForCustomer: nameForCustomer || null,
    franchiseeId: franchisee?.id ?? null,
    cityIds: cities.map((c) => c.id),
    shopIds:
      rest.forPickup && !(rest.forDeliveryNoLimit || rest.forDelivery)
        ? shops.map((s) => s.id)
        : [],
    value: isGeneral && isNew ? value : undefined,
    lifetime: typeof values.lifetime !== 'number' ? null : values.lifetime,
    oneTimePerUser: isGeneral ? values.oneTimePerUser : false,
    conditions: hasMinPrice
      ? []
      : conditions.map(({ products, ...rest }) => ({
          ...omit(rest, 'localId'),
          products: products.flatMap(({ variants }) =>
            variants.map(({ id }) => id)
          ),
        })),
    presents: hasDiscount
      ? []
      : presents.map(({ products, ...rest }) => ({
          ...omit(rest, 'localId', 'isActive'),
          products: products.flatMap(({ variants }) =>
            variants.map(({ id }) => id)
          ),
        })),
  };
};

export const addPromoCode = (data: PromoCodeValues) =>
  API.post(`/promocodes`, adaptPromoCode(data, true)).then(({ data }) =>
    Boolean(data)
  );

export const updatePromoCode = (params: {
  id: number;
  data: PromoCodeValues;
}) =>
  API.put(`/promocodes/${params.id}`, adaptPromoCode(params.data)).then(
    ({ data }) => Boolean(data)
  );

export const deletePromoCode = ({ id }: { id: number }) =>
  API.delete(`/promocodes/${id}`).then(({ data }) => data);

export const blockPromoCode = (params: { id: number; isActive: boolean }) =>
  API.patch(`/promocodes/${params.id}`, { isActive: params.isActive }).then(
    ({ data }) => data
  );

export const addPromoCodeValue = (params: {
  id: number;
  data: PromoCodeValue;
}) =>
  API.post(`/promocodes/${params.id}/values`, params.data).then(({ data }) =>
    Boolean(data)
  );

export const updatePromoCodeValues = (params: {
  id: number;
  data: {
    values: { id: number; startDate: string | null }[];
  };
}) =>
  API.patch(`/promocodes/${params.id}/values`, params.data).then(({ data }) =>
    Boolean(data)
  );

export const deletePromoCodeValue = (params: { id: number; valueId: number }) =>
  API.delete(`/promocodes/${params.id}/values/${params.valueId}`).then(
    ({ data }) => data
  );
